import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import { Card, Column, Group } from '../components/Kanban/interfaces/kanban'
import { KanbanActionType } from '../enums/actionType'

interface ExpandedGroup {
  isExpanded: boolean
  columnId: number
  groupId: string
}

export interface CardsMovedTemp {
  destinyColumnId: number
  cards: Card[]
}

interface KanbanState {
  columnsOriginal: Column[]
  columns: Column[]
  sortConfig: {
    sortBy: string
    sortOrder: string
  }
  actionType: KanbanActionType
  searchTerm: string
  filter: string
  cardsChecked: Card[]
  groupChecked?: Group
  isLoadingColumns: boolean
  isLoadingProcess: boolean
  columnsTemp: Column[]
  columnsTempOriginal: Column[]
  expandedGroups: ExpandedGroup[]
  isDraggingCardsChecked: boolean
  cardsMovedTemp: CardsMovedTemp
  isLoadedMainData: boolean
  isReadyOrganizedData: boolean
  isNotFinishedChangeState?: boolean
  isFiltered: boolean
  columnWithOutPermissionId?: number
  // Motivo que devuelve TransicionesDisponibles; undefined cuando el bloqueo es
  // estructural (no existe la transición) y no hay texto de negocio que mostrar
  columnWithOutPermissionMotivo?: string
  isOperationDate?: boolean
  doReset: boolean
  idDropColumnTemp?: number
  isFetchingBookings: boolean
  isFetchingVerticalStates: boolean
  isFetchingHorizontalStates: boolean
}

const initialState: KanbanState = {
  columnsOriginal: [],
  columns: [],
  sortConfig: { sortBy: 'operationDate', sortOrder: 'asc' },
  actionType: KanbanActionType.INITIAL,
  searchTerm: '',
  filter: '',
  cardsChecked: [],
  groupChecked: undefined,
  isLoadingColumns: false,
  isLoadingProcess: false,
  columnsTemp: [],
  columnsTempOriginal: [],
  expandedGroups: [],
  isDraggingCardsChecked: false,
  cardsMovedTemp: { destinyColumnId: 0, cards: [] },
  isLoadedMainData: false,
  isReadyOrganizedData: false,
  isNotFinishedChangeState: undefined,
  isFiltered: false,
  isOperationDate: true,
  doReset: false,
  isFetchingBookings: false,
  isFetchingVerticalStates: false,
  isFetchingHorizontalStates: false,
}

const kanbanSlice = createSlice({
  name: 'kanban',
  initialState,
  reducers: {
    setInitialState(state) {
      state.columnsOriginal = []
      state.columns = []
      state.sortConfig = { sortBy: 'operationDate', sortOrder: 'asc' }
      state.actionType = KanbanActionType.INITIAL
      state.searchTerm = ''
      state.filter = ''
      state.cardsChecked = []
      state.groupChecked = undefined
      state.isLoadingColumns = false
      state.isLoadingProcess = false
      state.columnsTemp = []
      state.expandedGroups = []
      state.isDraggingCardsChecked = false
      state.cardsMovedTemp = { destinyColumnId: 0, cards: [] }
      state.isNotFinishedChangeState = undefined
    },
    setInitialColumns(state) {
      state.columns = []
    },
    setSortConfig(
      state,
      action: PayloadAction<{ sortBy: string; sortOrder: string }>,
    ) {
      state.sortConfig = action.payload
    },
    setColumnsOriginal: (state, action: PayloadAction<{ value: Column[] }>) => {
      state.columnsOriginal = action.payload.value
    },
    setColumns: (state, action: PayloadAction<{ value: Column[] }>) => {
      state.columns = action.payload.value
    },
    setActionType(state, action: PayloadAction<{ value: KanbanActionType }>) {
      state.actionType = action.payload.value
    },
    setSearchTerm(state, action: PayloadAction<{ value: string }>) {
      state.searchTerm = action.payload.value
    },
    setFilter(state, action: PayloadAction<{ value: string }>) {
      state.filter = action.payload.value
    },
    setCardsChecked(state, action: PayloadAction<{ value: Card[] }>) {
      state.cardsChecked = action.payload.value
    },
    setGroupChecked(state, action: PayloadAction<{ value?: Group }>) {
      state.groupChecked = action.payload.value
    },
    setIsLoadingColumns(state, action: PayloadAction<{ value: boolean }>) {
      state.isLoadingColumns = action.payload.value
    },
    setIsLoadingProcess(state, action: PayloadAction<{ value: boolean }>) {
      state.isLoadingProcess = action.payload.value
    },
    setColumnsTemp(state, action: PayloadAction<{ value: Column[] }>) {
      state.columnsTemp = action.payload.value
    },
    setExpandedGroups(state, action: PayloadAction<{ value: ExpandedGroup }>) {
      const { groupId, columnId, isExpanded } = action.payload.value

      const existingItem = state.expandedGroups.find(
        (item) => item.groupId === groupId,
      )

      if (!existingItem) {
        state.expandedGroups.push({ groupId, columnId, isExpanded })
      } else {
        existingItem.isExpanded = isExpanded
      }

      state.expandedGroups.forEach((item) => {
        if (item.columnId === columnId && item.groupId !== groupId) {
          item.isExpanded = false
        }
      })
    },
    setIsDraggingCardsChecked(
      state,
      action: PayloadAction<{ value: boolean }>,
    ) {
      state.isDraggingCardsChecked = action.payload.value
    },
    setCardsMovedTemp(
      state,
      action: PayloadAction<{ value: Card[]; destinyColumnId: number }>,
    ) {
      state.cardsMovedTemp.cards = action.payload.value
      state.cardsMovedTemp.destinyColumnId = action.payload.destinyColumnId
    },
    setIsLoadedMainData(state, action: PayloadAction<{ value: boolean }>) {
      state.isLoadedMainData = action.payload.value
    },
    setIsReadyOrganizedData(state, action: PayloadAction<{ value: boolean }>) {
      state.isReadyOrganizedData = action.payload.value
    },
    setIsNotFinishedChangeState(
      state,
      action: PayloadAction<{ value?: boolean }>,
    ) {
      state.isNotFinishedChangeState = action.payload.value
    },
    setIsFiltered(state, action: PayloadAction<{ value: boolean }>) {
      state.isFiltered = action.payload.value
    },
    setColumnsTempOriginal(state, action: PayloadAction<{ value: Column[] }>) {
      state.columnsTempOriginal = action.payload.value
    },
    setColumnWithOutPermissionId(
      state,
      action: PayloadAction<{ value?: number; motivo?: string }>,
    ) {
      state.columnWithOutPermissionId = action.payload.value
      state.columnWithOutPermissionMotivo = action.payload.motivo
    },
    setIsOperationDate(state, action: PayloadAction<{ value: boolean }>) {
      state.isOperationDate = action.payload.value
    },
    setDoReset(state, action: PayloadAction<{ value: boolean }>) {
      state.doReset = action.payload.value
    },
    setIdDropColumnTemp(state, action: PayloadAction<{ value: number }>) {
      state.idDropColumnTemp = action.payload.value
    },
    setIsFetchingBookings(state, action: PayloadAction<{ value: boolean }>) {
      state.isFetchingBookings = action.payload.value
    },
    setIsFetchingVerticalStates(
      state,
      action: PayloadAction<{ value: boolean }>,
    ) {
      state.isFetchingVerticalStates = action.payload.value
    },
    setIsFetchingHorizontalStates(
      state,
      action: PayloadAction<{ value: boolean }>,
    ) {
      state.isFetchingHorizontalStates = action.payload.value
    },
  },
})

export const {
  setInitialState,
  setInitialColumns,
  setSortConfig,
  setColumnsOriginal,
  setColumns,
  setActionType,
  setSearchTerm,
  setFilter,
  setCardsChecked,
  setGroupChecked,
  setIsLoadingColumns,
  setIsLoadingProcess,
  setColumnsTemp,
  setExpandedGroups,
  setIsDraggingCardsChecked,
  setCardsMovedTemp,
  setIsLoadedMainData,
  setIsReadyOrganizedData,
  setIsNotFinishedChangeState,
  setIsFiltered,
  setColumnsTempOriginal,
  setColumnWithOutPermissionId,
  setIsOperationDate,
  setDoReset,
  setIdDropColumnTemp,
  setIsFetchingBookings,
  setIsFetchingVerticalStates,
  setIsFetchingHorizontalStates,
} = kanbanSlice.actions

const KanbanReducer = kanbanSlice.reducer

export default KanbanReducer
